import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Container,
  Grid,
  Typography,
  Button,
  Tabs,
  Tab,
  Rating,
  Divider,
  TextField,
  IconButton,
  Breadcrumbs,
  Link,
  Paper,
  Chip,
  useTheme,
  useMediaQuery,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  ShoppingCart as ShoppingCartIcon,
  Favorite as FavoriteIcon,
  FavoriteBorder as FavoriteBorderIcon,
  Share as ShareIcon,
  LocalShipping as ShippingIcon,
  Verified as VerifiedIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  NavigateNext as NavigateNextIcon,
  RecordVoiceOver as SuggestionIcon
} from '@mui/icons-material';
import { InventoryItem } from '../../utils/types';
import { getProductById } from '../../services/storeService';
import ProductImageGallery from '../../components/store/ProductImageGallery';
import { useScrollToTop } from '../../hooks/useScrollToTop';
import { useStoreSettings } from '../../contexts/StoreSettingsContext';
import ProductSuggestionModal from '../../components/store/ProductSuggestionModal';

// نموذج للمنتج
const productData = {
  id: 1,
  name: 'هاتف ذكي Galaxy S23 Ultra',
  description: 'هاتف ذكي بشاشة 6.8 بوصة وكاميرا 200 ميجابكسل ومعالج Snapdragon 8 Gen 2 وبطارية 5000 مللي أمبير',
  price: 4999,
  oldPrice: 5499,
  discount: 9,
  rating: 4.8,
  reviewCount: 120,
  stock: 50,
  sku: 'SM-S918B',
  brand: 'Samsung',
  category: 'الهواتف الذكية',
  tags: ['هاتف ذكي', 'سامسونج', 'جالاكسي', 'S23 Ultra'],
  features: [
    'شاشة Dynamic AMOLED 2X بحجم 6.8 بوصة ودقة QHD+',
    'كاميرا رئيسية بدقة 200 ميجابكسل',
    'معالج Snapdragon 8 Gen 2',
    'ذاكرة وصول عشوائي 12 جيجابايت',
    'مساحة تخزين 256 جيجابايت',
    'بطارية 5000 مللي أمبير',
    'نظام تشغيل Android 13',
    'قلم S Pen مدمج'
  ],
  images: [
    'https://via.placeholder.com/600x600?text=Galaxy+S23+Ultra+1',
    'https://via.placeholder.com/600x600?text=Galaxy+S23+Ultra+2',
    'https://via.placeholder.com/600x600?text=Galaxy+S23+Ultra+3',
    'https://via.placeholder.com/600x600?text=Galaxy+S23+Ultra+4'
  ],
  colors: [
    { name: 'أسود', code: '#000000' },
    { name: 'أبيض', code: '#FFFFFF' },
    { name: 'أخضر', code: '#2E8B57' },
    { name: 'بنفسجي', code: '#800080' }
  ],
  storage: [
    { size: '128GB', price: 4499 },
    { size: '256GB', price: 4999 },
    { size: '512GB', price: 5499 },
    { size: '1TB', price: 5999 }
  ],
  specifications: {
    'الشاشة': 'Dynamic AMOLED 2X، 6.8 بوصة، QHD+، 120Hz',
    'المعالج': 'Snapdragon 8 Gen 2',
    'الذاكرة': '12GB RAM',
    'التخزين': '256GB',
    'البطارية': '5000mAh',
    'الكاميرا الخلفية': '200MP + 12MP + 10MP + 10MP',
    'الكاميرا الأمامية': '12MP',
    'نظام التشغيل': 'Android 13، One UI 5.1',
    'الأبعاد': '163.4 × 78.1 × 8.9 مم',
    'الوزن': '233 جرام',
    'المقاومة': 'IP68 (مقاوم للماء والغبار)'
  },
  reviews: [
    {
      id: 1,
      user: 'أحمد محمد',
      rating: 5,
      date: '2023-05-15',
      comment: 'هاتف رائع بكل المقاييس، الكاميرا ممتازة والبطارية تدوم طويلاً.'
    },
    {
      id: 2,
      user: 'سارة علي',
      rating: 4,
      date: '2023-04-22',
      comment: 'أداء ممتاز وتصميم جميل، لكن السعر مرتفع قليلاً.'
    },
    {
      id: 3,
      user: 'محمد خالد',
      rating: 5,
      date: '2023-03-10',
      comment: 'قلم S Pen رائع ويضيف الكثير من المميزات للهاتف.'
    }
  ],
  relatedProducts: [
    {
      id: 2,
      name: 'هاتف ذكي iPhone 14 Pro Max',
      price: 5499,
      image: 'https://via.placeholder.com/300x300?text=iPhone+14'
    },
    {
      id: 3,
      name: 'هاتف ذكي Google Pixel 7 Pro',
      price: 3999,
      image: 'https://via.placeholder.com/300x300?text=Pixel+7'
    },
    {
      id: 4,
      name: 'هاتف ذكي Xiaomi 13 Pro',
      price: 3499,
      image: 'https://via.placeholder.com/300x300?text=Xiaomi+13'
    }
  ]
};

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`product-tabpanel-${index}`}
      aria-labelledby={`product-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `product-tab-${index}`,
    'aria-controls': `product-tabpanel-${index}`,
  };
}

const NewProductDetailPage: React.FC = () => {
  const { productId } = useParams<{ productId: string }>();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // حالة المنتج
  const [mainImage, setMainImage] = useState(productData.images[0]);
  const [quantity, setQuantity] = useState(1);
  const [selectedColor, setSelectedColor] = useState(productData.colors[0]);
  const [selectedStorage, setSelectedStorage] = useState(productData.storage[1]);
  const [tabValue, setTabValue] = useState(0);
  const [isFavorite, setIsFavorite] = useState(false);
  const [suggestionModalOpen, setSuggestionModalOpen] = useState(false);
  const { settings: storeSettings } = useStoreSettings();

  // التمرير إلى أعلى الصفحة عند تغيير المنتج
  useScrollToTop([productId]);

  // تغيير الصورة الرئيسية
  const handleImageChange = (image: string) => {
    setMainImage(image);
  };

  // تغيير الكمية
  const handleQuantityChange = (newQuantity: number) => {
    if (newQuantity >= 1 && newQuantity <= productData.stock) {
      setQuantity(newQuantity);
    }
  };

  // تغيير اللون
  const handleColorChange = (color: typeof selectedColor) => {
    setSelectedColor(color);
  };

  // تغيير التخزين
  const handleStorageChange = (storage: typeof selectedStorage) => {
    setSelectedStorage(storage);
  };

  // تغيير التاب
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // تبديل المفضلة
  const toggleFavorite = () => {
    setIsFavorite(!isFavorite);
  };

  // تنسيق السعر
  const formatPrice = (price: number) => {
    return price.toLocaleString('ar-SA') + ' ر.س';
  };

  // حساب السعر النهائي
  const finalPrice = selectedStorage.price;
  const oldPrice = productData.oldPrice;

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* فتات الخبز */}
      <Breadcrumbs
        separator={<NavigateNextIcon fontSize="small" />}
        aria-label="breadcrumb"
        sx={{ mb: 3 }}
      >
        <Link underline="hover" color="inherit" href="/store-new">
          الرئيسية
        </Link>
        <Link
          underline="hover"
          color="inherit"
          href={`/store-new/category/${productData.category}`}
        >
          {productData.category}
        </Link>
        <Typography color="text.primary">{productData.name}</Typography>
      </Breadcrumbs>

      {/* تفاصيل المنتج */}
      <Grid container spacing={4}>
        {/* صور المنتج */}
        <Grid item xs={12} md={6}>
          <Box sx={{ position: 'relative' }}>
            <ProductImageGallery
              mainImage={mainImage}
              additionalImages={productData.images.map((url, index) => ({
                id: `img_${index}`,
                url: url,
                isMain: index === 0,
                sortOrder: index,
                caption: `${productData.name} - صورة ${index + 1}`,
                createdAt: new Date().toISOString()
              }))}
              productName={productData.name}
            />

            {/* شارة الخصم */}
            {productData.discount > 0 && (
              <Chip
                label={`خصم ${productData.discount}%`}
                color="secondary"
                size="small"
                sx={{
                  position: 'absolute',
                  top: 16,
                  right: 16,
                  fontWeight: 'bold',
                  zIndex: 10
                }}
              />
            )}

            {/* أزرار المشاركة والمفضلة */}
            <Box
              sx={{
                position: 'absolute',
                top: 16,
                left: 16,
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                zIndex: 10
              }}
            >
              <IconButton
                onClick={toggleFavorite}
                sx={{
                  backgroundColor: 'white',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  '&:hover': {
                    backgroundColor: 'white',
                  },
                }}
              >
                {isFavorite ? (
                  <FavoriteIcon color="secondary" />
                ) : (
                  <FavoriteBorderIcon />
                )}
              </IconButton>

              <IconButton
                sx={{
                  backgroundColor: 'white',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  '&:hover': {
                    backgroundColor: 'white',
                  },
                }}
              >
                <ShareIcon />
              </IconButton>
            </Box>
          </Box>
        </Grid>

        {/* معلومات المنتج */}
        <Grid item xs={12} md={6}>
          <Box>
            {/* اسم المنتج */}
            <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold', mb: 1 }}>
              {productData.name}
            </Typography>

            {/* التقييم */}
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Rating value={productData.rating} precision={0.1} readOnly />
              <Typography variant="body2" sx={{ mr: 1, color: 'text.secondary' }}>
                ({productData.reviewCount} تقييم)
              </Typography>
              <Divider orientation="vertical" flexItem sx={{ mx: 2 }} />
              <Typography variant="body2" sx={{ color: 'success.main', display: 'flex', alignItems: 'center' }}>
                <VerifiedIcon fontSize="small" sx={{ mr: 0.5 }} />
                متوفر في المخزون
              </Typography>
            </Box>

            {/* السعر */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="h4" color="primary" sx={{ fontWeight: 'bold' }}>
                {formatPrice(finalPrice)}
              </Typography>

              {oldPrice > finalPrice && (
                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{ textDecoration: 'line-through', mr: 1 }}
                >
                  {formatPrice(oldPrice)}
                </Typography>
              )}

              {productData.discount > 0 && (
                <Typography variant="body2" color="secondary" sx={{ fontWeight: 'bold', mt: 0.5 }}>
                  توفير {formatPrice(oldPrice - finalPrice)} ({productData.discount}%)
                </Typography>
              )}
            </Box>

            {/* الوصف المختصر */}
            <Typography variant="body1" sx={{ mb: 3 }}>
              {productData.description}
            </Typography>

            <Divider sx={{ my: 3 }} />

            {/* اختيار اللون */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                اللون: {selectedColor.name}
              </Typography>

              <Box sx={{ display: 'flex', gap: 2 }}>
                {productData.colors.map((color) => (
                  <Box
                    key={color.name}
                    onClick={() => handleColorChange(color)}
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: '50%',
                      backgroundColor: color.code,
                      cursor: 'pointer',
                      border: selectedColor.name === color.name
                        ? `2px solid ${theme.palette.primary.main}`
                        : '2px solid transparent',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'scale(1.1)',
                      },
                    }}
                  />
                ))}
              </Box>
            </Box>

            {/* اختيار التخزين */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                التخزين
              </Typography>

              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                {productData.storage.map((storage) => (
                  <Button
                    key={storage.size}
                    variant={selectedStorage.size === storage.size ? 'contained' : 'outlined'}
                    color="primary"
                    onClick={() => handleStorageChange(storage)}
                    sx={{ minWidth: 80 }}
                  >
                    {storage.size}
                  </Button>
                ))}
              </Box>
            </Box>

            {/* الكمية */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                الكمية
              </Typography>

              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <IconButton
                  onClick={() => handleQuantityChange(quantity - 1)}
                  disabled={quantity <= 1}
                >
                  <RemoveIcon />
                </IconButton>

                <TextField
                  value={quantity}
                  onChange={(e) => {
                    const value = parseInt(e.target.value);
                    if (!isNaN(value)) {
                      handleQuantityChange(value);
                    }
                  }}
                  inputProps={{ min: 1, max: productData.stock, style: { textAlign: 'center' } }}
                  sx={{ width: 60, mx: 1 }}
                />

                <IconButton
                  onClick={() => handleQuantityChange(quantity + 1)}
                  disabled={quantity >= productData.stock}
                >
                  <AddIcon />
                </IconButton>

                <Typography variant="body2" sx={{ mr: 2, color: 'text.secondary' }}>
                  متوفر: {productData.stock} قطعة
                </Typography>
              </Box>
            </Box>

            {/* أزرار الشراء */}
            <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
              <Button
                variant="contained"
                color="primary"
                size="large"
                startIcon={<ShoppingCartIcon />}
                sx={{ flex: 2, py: 1.5, fontWeight: 'bold' }}
              >
                أضف إلى السلة
              </Button>

              <Button
                variant="outlined"
                color="primary"
                size="large"
                sx={{ flex: 1, py: 1.5, fontWeight: 'bold' }}
              >
                شراء الآن
              </Button>
            </Box>

            {/* زر اقتراح سعر أقل */}
            {storeSettings?.productSuggestion?.isEnabled && (
              <Button
                variant="outlined"
                color="secondary"
                size="large"
                startIcon={<SuggestionIcon />}
                onClick={() => setSuggestionModalOpen(true)}
                fullWidth
                sx={{ mb: 3, fontWeight: 'bold' }}
              >
                {storeSettings.productSuggestion.buttonText}
              </Button>
            )}

            {/* معلومات الشحن */}
            <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <ShippingIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                  معلومات الشحن
                </Typography>
              </Box>

              <Typography variant="body2">
                شحن مجاني للطلبات التي تزيد عن 200 ريال
              </Typography>
              <Typography variant="body2">
                التوصيل خلال 2-5 أيام عمل
              </Typography>
            </Paper>
          </Box>
        </Grid>
      </Grid>

      {/* تفاصيل إضافية (التابات) */}
      <Box sx={{ mt: 6 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            aria-label="product tabs"
            variant={isMobile ? 'scrollable' : 'standard'}
            scrollButtons={isMobile ? 'auto' : false}
            allowScrollButtonsMobile
            centered={!isMobile}
          >
            <Tab label="المواصفات" {...a11yProps(0)} />
            <Tab label="المميزات" {...a11yProps(1)} />
            <Tab label="التقييمات" {...a11yProps(2)} />
          </Tabs>
        </Box>

        {/* تاب المواصفات */}
        <TabPanel value={tabValue} index={0}>
          <Grid container spacing={2}>
            {Object.entries(productData.specifications).map(([key, value]) => (
              <Grid item xs={12} sm={6} key={key}>
                <Box sx={{ display: 'flex', borderBottom: '1px solid #eee', py: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', minWidth: 120 }}>
                    {key}:
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    {value}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </TabPanel>

        {/* تاب المميزات */}
        <TabPanel value={tabValue} index={1}>
          <Box component="ul" sx={{ pl: 2 }}>
            {productData.features.map((feature, index) => (
              <Typography component="li" key={index} variant="body1" sx={{ mb: 1 }}>
                {feature}
              </Typography>
            ))}
          </Box>
        </TabPanel>

        {/* تاب التقييمات */}
        <TabPanel value={tabValue} index={2}>
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
              تقييم العملاء
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <Box sx={{ textAlign: 'center', mr: 3 }}>
                <Typography variant="h3" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                  {productData.rating}
                </Typography>
                <Rating value={productData.rating} precision={0.1} readOnly size="large" />
                <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
                  {productData.reviewCount} تقييم
                </Typography>
              </Box>

              <Divider orientation="vertical" flexItem sx={{ mx: 3 }} />

              <Button variant="contained" color="primary">
                أضف تقييمك
              </Button>
            </Box>

            <Divider sx={{ mb: 3 }} />

            {/* قائمة التقييمات */}
            <Box>
              {productData.reviews.map((review) => (
                <Box key={review.id} sx={{ mb: 3, pb: 3, borderBottom: '1px solid #eee' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                      {review.user}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      {review.date}
                    </Typography>
                  </Box>

                  <Rating value={review.rating} readOnly size="small" sx={{ mb: 1 }} />

                  <Typography variant="body1">
                    {review.comment}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </TabPanel>
      </Box>

      {/* منتجات ذات صلة */}
      <Box sx={{ mt: 6 }}>
        <Typography variant="h5" component="h2" sx={{ fontWeight: 'bold', mb: 3 }}>
          منتجات ذات صلة
        </Typography>

        <Grid container spacing={3}>
          {productData.relatedProducts.map((product) => (
            <Grid item xs={12} sm={6} md={4} key={product.id}>
              <Paper
                sx={{
                  p: 2,
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  alignItems: 'center',
                  gap: 2,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-5px)',
                    boxShadow: '0 8px 16px rgba(0,0,0,0.1)'
                  }
                }}
              >
                <Box
                  component="img"
                  src={product.image}
                  alt={product.name}
                  sx={{
                    width: { xs: '100%', sm: 100 },
                    height: { xs: 200, sm: 100 },
                    objectFit: 'contain',
                    borderRadius: 1,
                    backgroundColor: '#f5f5f5',
                  }}
                />

                <Box sx={{ flex: 1, textAlign: { xs: 'center', sm: 'right' } }}>
                  <Typography
                    variant="subtitle1"
                    component={Link}
                    href={`/store-new/product/${product.id}`}
                    sx={{
                      fontWeight: 'bold',
                      color: 'text.primary',
                      textDecoration: 'none',
                      display: 'block',
                      mb: 1,
                      '&:hover': {
                        color: 'primary.main'
                      }
                    }}
                  >
                    {product.name}
                  </Typography>

                  <Typography variant="h6" color="primary" sx={{ fontWeight: 'bold' }}>
                    {formatPrice(product.price)}
                  </Typography>
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* نافذة اقتراح سعر أقل */}
      {storeSettings && storeSettings.productSuggestion && (
        <ProductSuggestionModal
          open={suggestionModalOpen}
          onClose={() => setSuggestionModalOpen(false)}
          product={{ ...productData, quantity: productData.stock, sellingPrice: finalPrice, id: productId || '1' } as any}
          settings={storeSettings.productSuggestion}
        />
      )}
    </Container>
  );
};

export default NewProductDetailPage;
